"use client";

import React, { useEffect, useState } from "react";
import styles from "./Accordion.module.scss";
import * as Accordion from "@radix-ui/react-accordion";
import { Plus, ArrowRight, ArrowUpRight } from "lucide-react";
import If from "@/components/common/If";

import Link from "next/link";
import RichText from "@/components/common/RichText";
import { Button } from "@/components/actions/Button";

/**
 *
 * Details
 *
 */
const Details = ({ items, blockLevel = false, className = "", ...props }) => {
  console.log("🔥 ACCORDION COMPONENT LOADED WITH ITEMS:", items?.length);
  const [currentOpen, setCurrentOpen] = useState<string | null>(null);
  useEffect(() => {
    if (items?.[0]?.open) {
      setCurrentOpen(items[0].title);
    }
  }, [items]);
  return (
    <section className={styles.details} {...props}>
      <Accordion.Root
        className={`w-full ${className}`}
        type="single"
        collapsible
        onValueChange={(value) => {
          setCurrentOpen(value);
        }}
        defaultValue={items?.[0]?.open ? items[0].title : ""}
      >
        {items.map((item, index) => {
          if (item.link) {
            return (
              <Link
                href={item.link}
                key={item.title}
                target={item.external ? "_blank" : "_self"}
              >
                <div
                  className={`border-b border-b-black pt-4 pb-3 w-full block ${blockLevel ? "py-6" : ""}`}
                >
                  <div className="flex justify-between items-center w-full py-6">
                    <h2
                      className={`font-hero text-black ${blockLevel ? "text-[1.25rem]" : "text-[1.25rem]"}`}
                    >
                      {item.title}
                    </h2>
                    <ArrowRight
                      className="transition-transform duration-300 hover:translate-x-1"
                      size={38}
                      color="black"
                      strokeWidth={0.5}
                    />
                  </div>
                  <If condition={item.detail}>
                    <p className="text-sm font-thin text-white">
                      {item.detail}
                    </p>
                  </If>
                </div>
              </Link>
            );
          } else {
            return (
              <Accordion.Item
                key={item.title}
                value={item.title}
                className="border-b border-b-black pt-4 pb-3 w-full block"
              >
                <Accordion.Header>
                  <Accordion.Trigger
                    className={`stack gap-2 w-full ${blockLevel ? "py-6" : "pb-2"} ${styles.trigger} text-left`}
                  >
                    <div className="flex justify-between items-center w-full">
                      <h2
                        className={`font-hero text-current ${blockLevel ? "text-[1.25rem]" : "text-[1.25rem]"}`}
                      >
                        {item.title}
                      </h2>
                      <Plus
                        className={`transition-all ease-in-out duration-300 ${
                          currentOpen === item.title ? "rotate-45" : ""
                        } relative block`}
                        size={38}
                        color="black"
                        strokeWidth={0.5}
                      />
                    </div>
                    <If condition={item.detail}>
                      <p className="text-sm font-thin text-white">
                        {item.detail}
                      </p>
                    </If>
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content
                  className={`py-10 data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp overflow-hidden ${styles.content}`}
                >
                  {item.description || item.children}
                  <If condition={item.content?.json}>
                    <RichText json={item.content?.json} />
                  </If>
                  <If condition={item.ctAsCollection?.items}>
                    <div>
                      <ul className="stack gap-2">
                        {(item.ctAsCollection?.items || [])
                          .filter((cta: any) =>
                            Boolean(cta?.title && cta?.link),
                          )
                          .map((cta: any, index: number) => (
                            <li key={cta?.sys?.id || `${cta?.title}-${index}`}>
                              {cta.type !== "link" ? (
                                <Button href={cta.link}>
                                  <p className="font-bold">{cta.title}</p>
                                </Button>
                              ) : (
                                <a
                                  href={cta.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex gap-2"
                                >
                                  {cta.title}
                                  <ArrowUpRight size={20} />
                                </a>
                              )}
                            </li>
                          ))}
                      </ul>
                    </div>
                  </If>
                </Accordion.Content>
              </Accordion.Item>
            );
          }
        })}
      </Accordion.Root>
    </section>
  );
};

export default Details;
